import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Heart, Sparkle } from "lucide-react";
import { toast } from "sonner";
import { saveCurrent, PILLAR_LABELS } from "@/lib/instant";
import { getProfile, getDeviceId } from "@/lib/profile";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

export default function Instant() {
  const navigate = useNavigate();
  const [instant, setInstant] = useState(null);
  const [limitMsg, setLimitMsg] = useState(null);
  const profile = getProfile();

  useEffect(() => {
    // L'Instant se découvre une fois l'espace créé — la sélection est conservée en local
    if (!localStorage.getItem("rav-token")) {
      navigate("/connexion?mode=register", { replace: true });
      return;
    }
    let selection = null;
    try {
      selection = JSON.parse(localStorage.getItem("rav-instant") || "null");
    } catch {
      /* ignore */
    }
    if (!selection?.minutes) {
      navigate("/");
      return;
    }
    // Le ressenti est facultatif : sans lui, on compose quand même (Instant générique)
    axios
      .post(`${API}/instants/compose`, {
        pillar: "respirer",
        minutes: selection.minutes,
        feeling: selection.need || null,
        need: selection.besoin || null,
        device_id: getDeviceId(),
      })
      .then(({ data }) => setInstant(data))
      .catch((err) => {
        if (err.response?.status === 402) setLimitMsg(err.response.data.detail);
        else {
          toast("Votre Instant n'a pas pu être préparé. Réessayez dans un instant.");
          navigate("/");
        }
      });
  }, [navigate]);

  const start = () => {
    saveCurrent(instant);
    navigate("/session");
  };

  return (
    <main className="rav-page" data-testid="instant-page">
      <motion.img
        src="/logo-accueil.webp"
        alt="Respire Aime Vibre"
        className="rav-page-logo"
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: EASE }}
      />
      {limitMsg && (
        <motion.div
          className="rav-limit-card"
          data-testid="instant-limit-card"
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: EASE }}
        >
          <Heart size={26} strokeWidth={1.4} color="#B88652" />
          <p className="rav-limit-text" data-testid="instant-limit-text">{limitMsg}</p>
          {(limitMsg.includes("7 Instants") || limitMsg.includes("découverte est terminée")) && (
            <button
              type="button"
              className="rav-primary-button"
              data-testid="instant-convert-now-button"
              onClick={() => navigate("/offres")}
            >
              Choisir ma formule RAV+ — sans engagement
            </button>
          )}
          <button
            type="button"
            className={limitMsg.includes("7 Instants") || limitMsg.includes("découverte est terminée") ? "rav-secondary-button" : "rav-primary-button"}
            data-testid="instant-limit-offers-button"
            onClick={() => navigate("/offres")}
          >
            Découvrir Respire Aime Vibre +
          </button>
          <button
            type="button"
            className="rav-text-link"
            data-testid="instant-limit-back-link"
            onClick={() => navigate("/")}
          >
            Revenir à l'accueil
          </button>
        </motion.div>
      )}
      {instant && !limitMsg && (
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.15, ease: EASE }}
        >
          <h1 className="rav-page-title" data-testid="instant-title">
            {profile?.prenom ? `${profile.prenom}, votre` : "Votre"} <span className="rav-instant-em">INSTANT</span> est prêt
          </h1>
          <p className="rav-page-sub" data-testid="instant-subtitle">
            {instant.minutes} minutes rien que pour vous
          </p>
          <div className="rav-steps-list">
            {instant.steps.map((step, i) => (
              <motion.div
                key={`${step.pillar}-${i}`}
                className="rav-instant-step"
                data-testid={`instant-step-${i}`}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 + i * 0.15, ease: EASE }}
              >
                <img src={`/needs/${step.pillar}.webp`} alt="" aria-hidden="true" className="rav-instant-step-icon" loading="lazy" decoding="async" />
                <span className="rav-instant-step-body">
                  <span className="rav-instant-step-head">
                    {step.label} — {step.minutes} min
                  </span>
                  <span className="rav-instant-step-title">« {step.title} »</span>
                  <span className="rav-instant-step-desc">{step.description}</span>
                </span>
              </motion.div>
            ))}
          </div>
          <button
            type="button"
            className="rav-primary-button"
            data-testid="start-instant-button"
            onClick={start}
          >
            <Sparkle size={16} strokeWidth={1.6} />
            Commencer mon Instant
          </button>
        </motion.div>
      )}
    </main>
  );
}
